import { api } from '@/lib/api';
import { formatEventDate } from '@/lib/utils';
import { formatCurrency } from '@/lib/utils';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

interface EventAnalytics {
  event: {
    id: string;
    name: string;
    eventDate: string;
    isPublished: boolean;
    isActive: boolean;
    daysUntilEvent: number;
    hasStarted: boolean;
  };
  seats: {
    total: number;
    available: number;
    sold: number;
    locked: number;
    blocked: number;
    occupancyRate: string;
    conversionRate: string;
  };
  revenue: {
    confirmed: number;
    pending: number;
    potential: number;
    mostSoldPrice: number;
  };
  sales: {
    totalPurchases: number;
    confirmedPurchases: number;
    pendingPurchases: number;
    failedPurchases: number;
    totalTicketsSold: number;
    averageTicketsPerPurchase: string;
  };
  charts: {
    salesByDay: Record<string, number>;
    priceDistribution: Record<string, number>;
  };
}

export default function EventAnalyticsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [analytics, setAnalytics] = useState<EventAnalytics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!id) {
      navigate('/dashboard/events');
      return;
    }

    const fetchAnalytics = async () => {
      try {
        setIsLoading(true);
        const data = await api.get<EventAnalytics>(`/events/${id}/analytics`);
        setAnalytics(data);
      } catch (err) {
        console.error('Error fetching analytics:', err);
        setError('No se pudo cargar las estadísticas del evento');
      } finally {
        setIsLoading(false);
      }
    };

    fetchAnalytics();
  }, [id, navigate]);

  // Despachar estadísticas para el editor: pendientes = locked, confirmadas = sold, disponibles = available
  useEffect(() => {
    try {
      const reservedPending = analytics?.seats.locked ?? 0;
      const confirmed = analytics?.seats.sold ?? 0;
      const available = analytics?.seats.available ?? 0;
      window.dispatchEvent(
        new CustomEvent('mapEditor:updateSeatStats', {
          detail: { reservedPending, confirmed, available },
        })
      );
    } catch (err) {
      console.warn('[EventAnalyticsPage] No se pudo despachar mapEditor:updateSeatStats:', err);
    }
  }, [analytics]);

  // Calcular porcentajes de distribución de asientos de forma memoizada
  // IMPORTANTE: Los hooks deben estar antes de cualquier return condicional
  const seatDistributionPercentages = useMemo(() => {
    if (!analytics || analytics.seats.total === 0) {
      return { sold: 0, available: 0, locked: 0, blocked: 0 };
    }
    const { seats } = analytics;
    return {
      sold: (seats.sold / seats.total) * 100,
      available: (seats.available / seats.total) * 100,
      locked: (seats.locked / seats.total) * 100,
      blocked: (seats.blocked / seats.total) * 100,
    };
  }, [analytics]);

  // Formatear fecha del evento de forma memoizada
  const formattedEventDate = useMemo(() => {
    if (!analytics) return '';
    return formatEventDate(analytics.event.eventDate);
  }, [analytics]);

  // Obtener máximo de ventas por día para el gráfico
  const maxSalesPerDay = useMemo(() => {
    if (!analytics) return 1;
    const salesValues = Object.values(analytics.charts.salesByDay || {});
    return salesValues.length > 0 ? Math.max(...salesValues) : 1;
  }, [analytics]);

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Cargando estadísticas...</p>
        </div>
      </div>
    );
  }

  if (error || !analytics) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="text-center py-12">
          <p className="text-red-600">{error || 'No se pudieron cargar las estadísticas'}</p>
          <button onClick={() => navigate('/dashboard/events')} className="btn-primary mt-4">
            Volver a Mis Eventos
          </button>
        </div>
      </div>
    );
  }

  const { event, seats, revenue, sales, charts } = analytics;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <Link
          to="/dashboard/events"
          className="text-blue-600 hover:text-blue-700 mb-4 inline-flex items-center"
        >
          ← Volver a Mis Eventos
        </Link>
        <h1 className="text-3xl font-bold text-gray-900 mt-2">{event.name}</h1>
        <p className="text-gray-600">
          {event.hasStarted ? 'Evento finalizado' : `Faltan ${event.daysUntilEvent} días`} •{' '}
          {formattedEventDate}
        </p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Asientos Ocupados */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Asientos Ocupados</h3>
            <svg
              className="h-5 w-5 text-blue-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
          </div>
          <p className="text-3xl font-bold text-gray-900">{seats.occupancyRate}</p>
          <p className="text-sm text-gray-500 mt-1">
            {seats.sold} de {seats.total} asientos
          </p>
        </div>

        {/* Ingresos Confirmados */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Ingresos Confirmados</h3>
            <svg
              className="h-5 w-5 text-green-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <p className="text-3xl font-bold text-green-600">{formatCurrency(revenue.confirmed)}</p>
          <p className="text-sm text-gray-500 mt-1">{sales.confirmedPurchases} ventas</p>
        </div>

        {/* Ingresos Pendientes */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Pendientes</h3>
            <svg
              className="h-5 w-5 text-yellow-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <p className="text-3xl font-bold text-yellow-600">{formatCurrency(revenue.pending)}</p>
          <p className="text-sm text-gray-500 mt-1">{sales.pendingPurchases} por confirmar</p>
        </div>

        {/* Ganancia Total Estimada */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Ganancia Total Estimada</h3>
            <svg
              className="h-5 w-5 text-purple-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
              />
            </svg>
          </div>
          <p className="text-3xl font-bold text-purple-600">{formatCurrency(revenue.potential)}</p>
          <p className="text-sm text-gray-500 mt-1">Aforo completo</p>
        </div>
      </div>

      {/* Estadísticas Detalladas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Distribución de Asientos */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Distribución de Asientos</h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">Vendidos</span>
                <span className="font-medium text-green-600">{seats.sold}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-green-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${seatDistributionPercentages.sold}%` }}
                ></div>
              </div>
            </div>

            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">Disponibles</span>
                <span className="font-medium text-blue-600">{seats.available}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${seatDistributionPercentages.available}%` }}
                ></div>
              </div>
            </div>

            {seats.locked > 0 && (
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600">Bloqueados (temp.)</span>
                  <span className="font-medium text-orange-600">{seats.locked}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-orange-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${seatDistributionPercentages.locked}%` }}
                  ></div>
                </div>
              </div>
            )}

            {seats.blocked > 0 && (
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600">Bloqueados (admin)</span>
                  <span className="font-medium text-gray-600">{seats.blocked}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-gray-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${seatDistributionPercentages.blocked}%` }}
                  ></div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Estadísticas de Ventas */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Estadísticas de Ventas</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Total de compras</span>
              <span className="font-medium text-gray-900">{sales.totalPurchases}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Confirmadas</span>
              <span className="font-medium text-green-600">{sales.confirmedPurchases}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Pendientes</span>
              <span className="font-medium text-yellow-600">{sales.pendingPurchases}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Rechazadas</span>
              <span className="font-medium text-red-600">{sales.failedPurchases}</span>
            </div>
            <hr className="my-3" />
            <div className="flex justify-between">
              <span className="text-gray-600">Tickets vendidos</span>
              <span className="font-medium text-gray-900">{sales.totalTicketsSold}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Promedio por compra</span>
              <span className="font-medium text-gray-900">{sales.averageTicketsPerPurchase}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Asiento más vendido</span>
              <span className="font-medium text-gray-900">
                {formatCurrency(revenue.mostSoldPrice)}
              </span>
            </div>
          </div>
        </div>

        {/* Ventas por Día */}
        {Object.keys(charts.salesByDay || {}).length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 lg:col-span-2">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Ventas por Día (Últimos 30 días)
            </h3>
            <div className="space-y-2">
              {Object.entries(charts.salesByDay)
                .sort(([dateA], [dateB]) => dateB.localeCompare(dateA))
                .slice(0, 10)
                .map(([date, count]) => (
                  <div key={date} className="flex items-center gap-4">
                    <span className="text-sm text-gray-600 w-32">
                      {formatEventDate(date, {
                        includeTime: false,
                        includeWeekday: false,
                        shortFormat: true,
                      })}
                    </span>
                    <div className="flex-1 bg-gray-200 rounded-full h-6 relative">
                      <div
                        className="bg-blue-500 h-6 rounded-full flex items-center justify-center text-white text-xs font-medium transition-all duration-300"
                        style={{
                          width: `${Math.max((count / maxSalesPerDay) * 100, 10)}%`,
                        }}
                      >
                        {count}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>

      {/* Acciones */}
      <div className="mt-8 flex justify-center gap-4">
        <Link
          to={`/dashboard/events/${id}/map`}
          className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50"
        >
          Ver Mapa
        </Link>
        <Link
          to={`/dashboard/events/${id}/payments`}
          className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50"
        >
          Pagos Pendientes
        </Link>
        <Link to={`/dashboard/events/${id}/attendees`} className="btn-primary px-6 py-3">
          Ver Asistentes
        </Link>
      </div>
    </div>
  );
}
